import React, { useEffect, useRef, useState } from 'react';
import QuestionWrapper from '../../core/ui/question_wrapper';
import QuestionHeading from '../../core/ui/question_heading';
import QuestionSubheading from '../../core/ui/question_subheading';
import { loadDocusealEmbed } from '../../../helpers/docuseal_embed';

// Floor for the DocuSeal form's height so a tall heading/subheading above it
// can't squeeze its scrollport down to something unusably small.
const MIN_FORM_HEIGHT = 360;
// Breathing room kept below the form so it doesn't butt against the bottom
// edge of the viewport.
const MIN_BOTTOM_MARGIN = 32;

const CHECKING = 'checking';
const SIGNED = 'signed';
const SIGNING = 'signing';

const placeholderStyle = {
  border: '1px dashed #cbd5e1',
  borderRadius: '10px',
  padding: '32px 24px',
  textAlign: 'center',
  color: '#475569',
  fontSize: '14px',
  lineHeight: 1.5,
};

const signedPanelStyle = {
  border: '1px solid #cbd5e1',
  borderRadius: '10px',
  padding: '24px',
  color: '#334155',
  fontSize: '14px',
  lineHeight: 1.6,
};

const redoButtonStyle = {
  marginTop: '16px',
  padding: '10px 18px',
  border: '1px solid #94a3b8',
  borderRadius: '999px',
  background: '#ffffff',
  color: '#334155',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
};

const formatConsentDate = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/**
 * Renders a DocuSeal embedded signing form for a document_signing question.
 * Without an onRequestSubmission callback (the admin builder preview) it
 * renders the template's shared form in DocuSeal's preview mode — the real
 * document and fields, but nothing can be submitted and no signature request
 * is created. Falls back to a placeholder when no template slug is known.
 *
 * Requesting a submission mints a new DocuSeal form, so an already-stored
 * consent has to be ruled out first: otherwise returning to a signed step opens
 * a second blank copy of a document the mentee has already signed, and the
 * signature it collects is discarded. A mentee who needs to correct something
 * redoes the consent, which supersedes the stored one and reopens the form.
 */
const DocumentSigningQuestion = ({
  question,
  current,
  total,
  onRequestSubmission,
  onRequestSigningState,
  onRedo,
  onComplete,
  onBack,
  previewSlug,
}) => {
  const containerRef = useRef(null);
  const [embedSrc, setEmbedSrc] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState(CHECKING);
  const [signedConsent, setSignedConsent] = useState(null);
  const [redoing, setRedoing] = useState(false);

  const previewOnly = typeof onRequestSubmission !== 'function';
  const canCheckSignature = !previewOnly && typeof onRequestSigningState === 'function';
  const src = previewOnly ? (previewSlug ? `https://docuseal.com/d/${previewSlug}` : null) : embedSrc;

  useEffect(() => {
    setEmbedSrc(null);
    setError(null);
    setSignedConsent(null);

    if (previewOnly) {
      setPhase(SIGNING);
      if (previewSlug) loadDocusealEmbed().catch(() => {});
      return undefined;
    }

    if (!canCheckSignature) {
      setPhase(SIGNING);
      return undefined;
    }

    let cancelled = false;
    setPhase(CHECKING);

    onRequestSigningState()
      .then((state) => {
        if (cancelled) return;

        if (state && state.signed) {
          setSignedConsent(state);
          setPhase(SIGNED);
        } else {
          setPhase(SIGNING);
        }
      })
      .catch((requestError) => {
        if (!cancelled) {
          setError(
            (requestError && requestError.message)
              || "We couldn’t check whether you’ve already signed this document. Please try again."
          );
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id, previewOnly, previewSlug]);

  useEffect(() => {
    if (previewOnly || phase !== SIGNING) return undefined;

    let cancelled = false;

    Promise.all([loadDocusealEmbed(), onRequestSubmission()])
      .then(([, submission]) => {
        if (!cancelled) setEmbedSrc(submission.embed_src);
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError.message);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, question.id, previewOnly]);

  useEffect(() => {
    if (!src || !containerRef.current) return undefined;

    const form = containerRef.current.ownerDocument.createElement('docuseal-form');
    form.setAttribute('data-src', src);
    form.setAttribute('data-with-title', 'false');
    form.setAttribute('data-logo', '');
    if (previewOnly) form.setAttribute('data-preview', 'true');

    // DocuSeal sticks the active field's input card to the bottom of the
    // form's own scrollport rather than the browser viewport, so without a
    // bounded height and overflow here it has no scrollport to stick within
    // — the card falls into normal document flow below the PDF instead of
    // floating over it. The form's own background is transparent, so it also
    // needs an explicit background or the page behind shows through past the
    // document. Everything inside the shadow root is DocuSeal's, so this is
    // the extent of what we can style from here without data-custom-css.
    form.style.display = 'block';
    form.style.overflow = 'auto';
    form.style.background = '#ffffff';

    const handleCompleted = (event) => {
      if (typeof onComplete === 'function') onComplete(event.detail || {});
    };

    form.addEventListener('completed', handleCompleted);
    containerRef.current.appendChild(form);

    // Cap the form's height to whatever viewport space is left below it so
    // the form scrolls the document internally instead of also growing the
    // outer page — two scrollbars stacked back to back otherwise look like
    // one long scrollbar that runs past the bottom of the document.
    const view = form.ownerDocument.defaultView || window;
    const resize = () => {
      const available = view.innerHeight - form.getBoundingClientRect().top - MIN_BOTTOM_MARGIN;
      form.style.maxHeight = `${Math.max(MIN_FORM_HEIGHT, available)}px`;
    };
    resize();
    view.addEventListener('resize', resize);

    return () => {
      view.removeEventListener('resize', resize);
      form.removeEventListener('completed', handleCompleted);
      form.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const handleRedo = () => {
    if (redoing || typeof onRedo !== 'function') return;

    setRedoing(true);
    setError(null);

    onRedo()
      .then(() => {
        setRedoing(false);
        setSignedConsent(null);
        setPhase(SIGNING);
      })
      .catch((redoError) => {
        setRedoing(false);
        setError(redoError.message);
      });
  };

  const handleContinue = () => {
    if (typeof onComplete !== 'function') return;

    onComplete({ submission_id: signedConsent?.docuseal_submission_id || 'signed' });
  };

  const showSigned = phase === SIGNED && signedConsent;
  const consentDate = showSigned ? formatConsentDate(signedConsent.consented_at) : null;

  return (
    <QuestionWrapper
      current={current}
      total={total}
      wide
      isDisabled={redoing}
      showNavButtons={Boolean(showSigned)}
      onSubmit={handleContinue}
      onBack={onBack}
    >
      <QuestionHeading>{question.heading || 'Untitled question'}</QuestionHeading>
      {question.subHeading && <QuestionSubheading>{question.subHeading}</QuestionSubheading>}
      {previewOnly && !src && (
        <div style={placeholderStyle}>
          <strong>DocuSeal signing form</strong>
          <p style={{ margin: '8px 0 0' }}>
            Template: {question.docusealTemplateName || question.docusealTemplateId || 'none selected'}
          </p>
          <p style={{ margin: '8px 0 0' }}>Pick a template to preview the document here.</p>
        </div>
      )}
      {showSigned && (
        <div style={signedPanelStyle}>
          <strong>You&rsquo;ve already signed this</strong>
          <p style={{ margin: '8px 0 0' }}>
            {signedConsent.title || 'This document'}
            {consentDate ? ` was signed on ${consentDate}.` : ' is on file.'} We emailed you a copy for your records.
          </p>
          <p style={{ margin: '8px 0 0' }}>
            Continue to the next step, or redo it if something needs to be corrected. Redoing replaces the copy we use
            going forward.
          </p>
          <button type='button' style={redoButtonStyle} onClick={handleRedo} disabled={redoing}>
            {redoing ? 'Reopening the document…' : 'Redo this document'}
          </button>
        </div>
      )}
      {!previewOnly && error && <div style={placeholderStyle}>{error}</div>}
      {!previewOnly && !error && phase === CHECKING && <div style={placeholderStyle}>Checking your documents…</div>}
      {!previewOnly && !error && phase === SIGNING && !embedSrc && (
        <div style={placeholderStyle}>Preparing your document…</div>
      )}
      <div ref={containerRef} />
    </QuestionWrapper>
  );
};

export default DocumentSigningQuestion;
